import { useAtomValue } from 'jotai'
import React, { useEffect, useState } from 'react'
import { Outlet, useLocation, useParams, useNavigate } from 'react-router-dom'

import GuideNavbar from './guide.navbar'
import { toast } from '@app/components/ui/Toast/toast'

import { appLink } from '@app/config/appLink'
import { ErrorPage } from '@app/routes/ErrorPage'
import { ERROR_403_MESSAGE } from '@app/app-constants'
import { allSubTabsAtom, subTopicsAtomFamily } from '@app/state/subTopicsAtom'
import { GuideLayoutSkeleton } from '@app/components/Guide/layout/GuideLayoutSkeleton'
import { GuideMainTopics } from '@app/components/Guide/types/guide.types'
import { groupByMainTopic } from '@app/components/Guide/utils/groupByMainTopic'
import { GuideSubTopicNavigation } from '../components/GuideSubTopicNavigation'

type GuideLayoutProps = { children?: React.ReactNode }

export const GuideLayout = ({ children }: GuideLayoutProps) => {
  const navigate = useNavigate()
  const { mainTopic } = useParams()
  const { pathname } = useLocation()

  const [showAllTopics, setShowAllTopics] = useState(false)

  useEffect(() => {
    setShowAllTopics(false)
  }, [mainTopic])

  const subTabByTopic = useAtomValue(subTopicsAtomFamily(mainTopic as string))
  const allSubTabs = useAtomValue(allSubTabsAtom)

  const isLoading = showAllTopics ? allSubTabs.isLoading : subTabByTopic.isLoading
  const error = showAllTopics ? allSubTabs.error : subTabByTopic.error
  const subTab = showAllTopics ? allSubTabs.data : subTabByTopic.data

  useEffect(() => {
    if (error?.message === ERROR_403_MESSAGE) {
      toast({
        type: 'error',
        description:
          'You do not have permission to view this content. Please contact your administrator.',
        position: 'top-right',
      })
      navigate(appLink.homepage.path)
    }
  }, [error, navigate])

  if (isLoading) return <GuideLayoutSkeleton />
  if (error) return <ErrorPage />

  const isGetStartedPage = pathname === `${appLink.guidePage.path}/${GuideMainTopics.GET_STARTED}`
  const isGlossaryPage = pathname === `${appLink.guidePage.path}/glossary`

  if (isGetStartedPage) {
    return (
      <div className="flex flex-col w-full h-screen overflow-y-auto overflow-x-hidden  font-inter bg-background">
        <GuideNavbar />
        <Outlet />
      </div>
    )
  }

  return (
    <div className="relative flex h-screen w-full flex-col overflow-y-auto overflow-x-hidden bg-background font-inter">
      <GuideNavbar />
      <div
        id="guide-layout"
        className="
          relative mx-auto flex w-full flex-1 min-h-0 overflow-y-auto overflow-x-hidden
          max-w-[1400px] gap-4 lg:gap-6
          sm:px-4 lg:px-6 xl:px-8
          scrollbar-thin scrollbar-track-transparent scrollbar-thumb-black/20
        "
      >
        {!isGlossaryPage ? (
          <GuideSubTopicNavigation
            navigation={groupByMainTopic(subTab || [], showAllTopics ? undefined : mainTopic)}
            handleClickTopic={() => setShowAllTopics((v) => !v)}
            allTopicsActive={showAllTopics}
          />
        ) : (
          <div className="w-64 shrink-0" />
        )}

        <main className="flex flex-1 min-w-0 flex-col max-w-[900px] md:px-0 px-2">
          <div className="flex-1 min-w-0 w-full">
            <Outlet />
            {children}
          </div>
        </main>
      </div>
    </div>
  )
}

export default GuideLayout
